/**
 * Message list — the middle pane.
 *
 * Rows are plain, fixed-height buttons paginated from the bridge; search
 * results use the same rows with a banner explaining the mode. Empty states
 * say what is real: loading, no messages, offline-with-cache.
 */
import type { JSX } from "preact";
import { useState } from "preact/hooks";

import type { SearchCriteria } from "../bridge/types.ts";
import {
  clearSearch,
  loadOlder,
  openFolder,
  openMessage,
  runSearch,
  toggleFlag,
} from "../state/session.ts";
import {
  activeFolderId,
  activeMessageId,
  connection,
  endOfMailbox,
  listError,
  listLoading,
  messages,
  searchMode,
  selectMessage,
  type ConnectionState,
} from "../state/store.ts";

import { formatListDate, formatSize } from "./format.ts";
import { FlagIcon, MailIcon, SearchIcon } from "./icons.tsx";

/**
 * One honest sentence per connection state, for an empty list pane.
 */
const EMPTY_COPY: Readonly<Record<ConnectionState, string>> = {
  booting: "Starting the local mail engine…",
  "no-account": "Add an account to see your messages.",
  connected: "No messages in this folder.",
  error: "The mail bridge is unreachable. Check the connection and retry.",
};

/**
 * Renders the active folder's messages.
 *
 * @returns The list section element.
 */
export function MessageList(): JSX.Element {
  const state = connection.value;
  const hasRows = messages.value.length > 0;
  if (state !== "connected" || (!hasRows && !searchMode.value)) {
    const note =
      state === "connected"
        ? activeFolderId.value === null
          ? "Choose a folder to see its messages."
          : EMPTY_COPY.connected
        : EMPTY_COPY[state];
    return (
      <div class="empty-state" role="note">
        <MailIcon />
        <p>{note}</p>
      </div>
    );
  }
  return (
    <>
      {searchMode.value ? <SearchBanner /> : null}
      {hasRows ? <Rows /> : <ListEmpty note={EMPTY_COPY.connected} />}
      {searchMode.value ? null : <OlderButton />}
    </>
  );
}

/**
 * The banner over search results, with the way out.
 *
 * @returns The banner element.
 */
function SearchBanner(): JSX.Element {
  return (
    <div class="search-banner" role="status">
      <span>
        Search results — {messages.value.length}
        {messages.value.length === 1 ? " match" : " matches"}
      </span>
      <button
        type="button"
        class="ghost-button"
        onClick={() => {
          clearSearch();
        }}
      >
        Clear search
      </button>
    </div>
  );
}

/**
 * The list rows.
 *
 * @returns The ordered list element.
 */
function Rows(): JSX.Element {
  return (
    <ol class="message-list" aria-label="Messages">
      {messages.value.map((message) => (
        <MessageRow message={message} key={message.id} />
      ))}
    </ol>
  );
}

/**
 * One row: the opening button plus the flag toggle beside it.
 *
 * @param props - The row's message and identity.
 * @param props.message - The list item.
 * @returns The row element.
 */
function MessageRow(props: {
  readonly message: (typeof messages.value)[number];
}): JSX.Element {
  const { message } = props;
  const open = (): void => {
    selectMessage(message.id);
    void openMessage(message.id);
  };
  const flagLabel = message.flagged
    ? `Remove flag from message from ${message.from}`
    : `Flag message from ${message.from}`;
  return (
    <li class={`message-row${message.unread ? " is-unread" : ""}`}>
      <button
        type="button"
        class="message-open"
        aria-current={activeMessageId.value === message.id ? "true" : undefined}
        onClick={open}
      >
        {message.unread ? (
          <>
            <span class="unread-dot" aria-hidden="true" />
            <span class="visually-hidden">Unread. </span>
          </>
        ) : null}
        <span class="message-from">{message.from}</span>
        <span class="message-date">
          {message.date === null ? "" : formatListDate(message.date)}
        </span>
        <span class="message-subject">{message.subject ?? "(no subject)"}</span>
        <span class="message-size">{formatSize(message.size)}</span>
      </button>
      <button
        type="button"
        class="flag-button"
        aria-pressed={message.flagged}
        aria-label={flagLabel}
        onClick={() => {
          void toggleFlag(message.id, String.raw`\Flagged`, !message.flagged);
        }}
      >
        <FlagIcon filled={message.flagged} />
      </button>
    </li>
  );
}

/**
 * The genuine empty-list sentence inside a connected folder.
 *
 * @param props - The note.
 * @param props.note - The sentence.
 * @returns The empty state element.
 */
function ListEmpty(props: { readonly note: string }): JSX.Element {
  return (
    <div class="empty-state" role="note">
      <MailIcon />
      <p>{props.note}</p>
    </div>
  );
}

/**
 * The load-older affordance, shown while pages remain.
 *
 * @returns The footer element, or `null` at the mailbox floor.
 */
function OlderButton(): JSX.Element | null {
  if (endOfMailbox.value && listError.value === null && !listLoading.value) {
    return null;
  }
  return (
    <div class="list-footer">
      {listLoading.value ? <p role="status">Loading messages…</p> : null}
      {listError.value === null ? null : (
        <div class="pane-alert" role="alert">
          <p>{listError.value}</p>
          <button type="button" class="ghost-button" onClick={retryList}>
            Retry
          </button>
        </div>
      )}
      {endOfMailbox.value || listLoading.value ? null : (
        <button
          type="button"
          class="ghost-button"
          onClick={() => {
            void loadOlder();
          }}
        >
          Load older messages
        </button>
      )}
    </div>
  );
}

/**
 * Re-opens the active folder after a list failure.
 */
function retryList(): void {
  if (searchMode.value) {
    return;
  }
  void openFolder(activeFolderId.value ?? "INBOX");
}

/**
 * The search box over the active folder.
 *
 * @returns The search form element, or `null` outside a folder.
 */
export function SearchBox(): JSX.Element | null {
  const [value, setValue] = useState("");
  if (connection.value !== "connected" || activeFolderId.value === null) {
    return null;
  }
  const submit = (kind: SearchCriteria["kind"]): void => {
    const text = value.trim();
    if (text === "" && kind !== "unseen") {
      return;
    }
    void runSearch(kind === "unseen" ? { kind } : { kind, value: text });
  };
  return (
    <form
      class="search-box"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        submit("subject");
      }}
    >
      <input
        type="search"
        class="search-input"
        aria-label={`Search ${activeFolderId.value} by subject`}
        placeholder="Search this folder…"
        value={value}
        onInput={(event) => {
          setValue(event.currentTarget.value);
        }}
      />
      <button
        type="submit"
        class="icon-button"
        aria-label="Search by subject"
        disabled={value.trim() === ""}
      >
        <SearchIcon />
      </button>
    </form>
  );
}
